import { z } from 'zod';
import {
  AttachmentSchema,
  CreateChatDataSchema,
  MAX_MESSAGES,
  MAX_TEXT_LEN,
  MessageSchema,
  MetaSchema,
  Rfc3339DateTime,
} from './create-chat.js';
import { TurnArtifactSchema } from './workstream-agent.js';

/**
 * `POST /api/v1/workstreams/chat` — a chat turn inside an **existing**
 * workstream. Async (ADR-0037): the BFF submits the turn and returns `202`
 * with a `turnId`; the SPA polls `/workstreams/chat/poll` for the result.
 * Distinct from `/workstreams/create/chat` (ADR-0024, frozen —
 * creates nothing, no `workstreamId`): this endpoint is scoped to a real
 * workstream and also carries the Librarian "invite agent" hint
 * (`agentName` → `AgentInvokeRequest.agentName`).
 *
 * Stateless like the create-flow pair (no router path param — the router
 * only does exact-string method+path matching, see `handlers/router.ts` —
 * so `workstreamId` travels in the body, same as `AgentInvokeRequest`):
 * `messages` is the client-held transcript, resent each call. No
 * server-side persistence yet — `WorkstreamState` lives in-memory in the
 * Python pipeline, persisted in DynamoDB (see ADR-0030 / ADR-0034).
 */
export const WorkstreamChatRequestSchema = z.strictObject({
  workstreamId: z.string().min(1),
  /**
   * Every workstream has at least one focus (product-archaeology §2.8 —
   * `Workstream (1) -> Focus (many) -> Message (many)`). No focus CRUD or
   * persistence yet — the SPA sends the constant `'default'` implicit focus
   * until multi-focus switching lands; the field exists now so the contract
   * doesn't need a breaking change when it does.
   */
  focusId: z.string().min(1),
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
  question: z.string().min(1).max(MAX_TEXT_LEN),
  questionCreatedAt: Rfc3339DateTime.optional(),
  /** Invite-agent hint — forwarded as-is to `AgentInvokeRequest.agentName`. */
  agentName: z.string().optional(),
  /**
   * Turn kind — forwarded to `AgentInvokeRequest.action`. `exitDiscovery`
   * transitions the workstream from discovery mode back to normal routing;
   * absent means a plain chat turn.
   */
  action: z.enum(['chat', 'exitDiscovery']).optional(),
  /**
   * PDF attached to this turn (additive-optional, ADR-0024 §7) — forwarded
   * as-is to `AgentInvokeRequest.attachment`, where the agent runtime feeds
   * it to the model as a Bedrock Converse document block. Turn-scoped: not
   * persisted, not echoed back in the transcript.
   */
  attachment: AttachmentSchema.optional(),
});
export type WorkstreamChatRequest = z.infer<typeof WorkstreamChatRequestSchema>;

/** The Librarian's routing mode for a workstream (see `AgentInvokeRequest.action`). */
export const WorkstreamModeSchema = z.enum(['discovery', 'invite', 'normal']);
export type WorkstreamMode = z.infer<typeof WorkstreamModeSchema>;

/** The consensus tracker's verdict for a turn (Python `ConsensusStatus`). */
export const WorkstreamConsensusSchema = z.enum(['agreed', 'conflicted', 'incomplete']);
export type WorkstreamConsensus = z.infer<typeof WorkstreamConsensusSchema>;

/**
 * Submit ack (ADR-0037): the chat turn now executes asynchronously, so
 * `POST /workstreams/chat` returns `202` with the turn id to poll plus the
 * echoed, server-stamped user message (rendered immediately by the SPA).
 *
 * `looseObject`, like the envelope around it and the poll `data` below, because
 * these are READ-side shapes and their consumer is a browser holding a bundle
 * we cannot redeploy (AC10 tolerant-out). Strict `data` meant the first server
 * field a stale bundle had not heard of threw the WHOLE response and failed the
 * turn — which is exactly how adding `seq` took down every chat turn on dev.
 * The envelopes were already tolerant; the `data` objects inside them were not,
 * so the protection stopped one level short of where it was needed. Strictness
 * still guards the direction that matters: the BFF narrows agent output with
 * the strict `TurnEventSchema`, and REQUEST schemas stay strict.
 */
export const WorkstreamChatSubmitDataSchema = z.looseObject({
  turnId: z.string().min(1),
  userMessage: MessageSchema,
});
export type WorkstreamChatSubmitData = z.infer<typeof WorkstreamChatSubmitDataSchema>;

/** `{ data, meta }` envelope (tolerant-out, ADR-0024 §11 convention). */
export const WorkstreamChatSubmitResponseSchema = z.looseObject({
  data: WorkstreamChatSubmitDataSchema,
  meta: MetaSchema,
});
export type WorkstreamChatSubmitResponse = z.infer<typeof WorkstreamChatSubmitResponseSchema>;

/** `POST /api/v1/workstreams/chat/poll` — read an async turn's status/result. */
export const WorkstreamChatPollRequestSchema = z.strictObject({
  workstreamId: z.string().min(1),
  turnId: z.string().min(1),
});
export type WorkstreamChatPollRequest = z.infer<typeof WorkstreamChatPollRequestSchema>;

/**
 * One progress checkpoint inside a running turn (ADR-0042) — `kind` is a
 * free string (`routed` / `stage` / `tool` today, additive), `label` the
 * human-readable line the SPA renders verbatim.
 */
/**
 * Upper bound on `seq`. Not tied to {@link MAX_TURN_EVENTS}: `seq` is the
 * ordinal of EMISSION, which deliberately keeps counting past the persisted
 * cap (see the field docs), and Phase 3 token streaming pushes many more
 * events than a turn persists. The bound exists only to keep a pathological
 * value out of a consumer that will range over it — an unbounded `seq` reaching
 * a gap-detector loop or an array pre-allocation is a frozen tab.
 */
export const MAX_TURN_EVENT_SEQ = 100_000;

/** The `seq` field, shared by the strict narrowing schema and its read-side twin. */
const TurnEventSeq = z.number().int().nonnegative().max(MAX_TURN_EVENT_SEQ).optional();

/**
 * `tokens` — the turn's cumulative OUTPUT tokens when this checkpoint was
 * emitted (the "↓ 2.6k tokens" of the SPA status line). Optional: absent on
 * events from an agent that predates it and on terminal events emitted outside
 * the metered run (e.g. the workstream-lock rejection). Display only — billing
 * reads the turn's `usage` (ADR-0063), never this. Bounded — and note the BFF
 * drops the WHOLE event (label too) when this fails the strict parse, so the
 * agent clamps to this same bound rather than sending a value it would refuse.
 */
export const MAX_TURN_EVENT_TOKENS = 100_000_000;
const TurnEventTokens = z.number().int().nonnegative().max(MAX_TURN_EVENT_TOKENS).optional();

/**
 * `seq` — ordinal of emission within a turn (ADR-0058 Phase 0.5): ordering for
 * the long-poll's `since`, and gap detection for the push channel's reorder
 * buffer.
 *
 * **It counts emissions, not array slots.** `_report` stops appending at
 * {@link MAX_TURN_EVENTS} and the BFF `slice`s to the same cap — both drop the
 * TAIL. If `seq` were the array index the survivors would be a dense `0..49`
 * and a turn that emitted 120 checkpoints would look complete, so the one loss
 * mode gap-detection must catch would be the one it cannot see. Counting
 * emissions makes truncation show up as a jump.
 *
 * **Optional per EVENT, not per turn.** The producer has three append sites —
 * the `_report` reporter plus two direct appends (workstream-lock rejection,
 * item-too-large degrade) — so a client must tolerate a `seq`-less event
 * mid-stream and must not treat it as a gap.
 *
 * The Python mirror (`contracts/pipeline.py:TurnEvent`) is the other half and
 * defaults to `extra="ignore"`, so it silently STRIPS unknown keys: adding
 * `seq` only at the emission call site would not survive a round trip.
 */
export const TurnEventSchema = z.strictObject({
  at: Rfc3339DateTime,
  kind: z.string().min(1).max(40),
  label: z.string().min(1).max(500),
  seq: TurnEventSeq,
  tokens: TurnEventTokens,
});
export type TurnEvent = z.infer<typeof TurnEventSchema>;

/** Python-side cap (`MAX_TURN_EVENTS`) mirrored so the parse bounds the payload. */
export const MAX_TURN_EVENTS = 50;

/**
 * A completed turn's result — the assistant reply plus the Librarian extras
 * the old sync response carried. `mode` gates the invite / exit-discovery
 * affordances; `consensus`/`conflictDetail` drive the conflict banner;
 * `agents` is the "who handled it" indicator (execution order).
 */
// Read-side too (it exists only inside the poll `data`, never as a narrowing
// defence), and it is the shape that has grown most — `mode`, `consensus`,
// `ledger` and `agents` were each added after the fact. Every one of those was
// the same latent outage as `seq`.
export const WorkstreamTurnResultSchema = z.looseObject({
  reply: MessageSchema,
  mode: WorkstreamModeSchema.optional(),
  consensus: WorkstreamConsensusSchema.optional(),
  conflictDetail: z.string().optional(),
  agents: z.array(z.string()).optional(),
  /**
   * ADR-0080 AB-1-4: which of `agents` answered as a workstream's custom or
   * curated member — run by agentforge, shown in the elevated bubble (D7).
   */
  memberAgents: z
    .array(
      z.strictObject({
        agentId: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
        displayName: z.string().min(1).max(80),
        version: z.number().int().positive(),
      }),
    )
    .optional(),
  /** Deliverables written this turn (ADR-0040) — download link + inline preview. */
  artifacts: z.array(TurnArtifactSchema).optional(),
  /**
   * The workstream's cross-turn artifact ledger (ADR-0041, narrowed off
   * `workstreamState.artifacts` per ADR-0042/Stage 3c) — metadata only
   * (`url`/`preview` stripped server-side); feeds the persistent
   * ArtifactWorkspace panel so deliverables outlive the turn that wrote them.
   */
  ledger: z.array(TurnArtifactSchema).optional(),
  /**
   * An agent handed the workstream on this turn (ADR-0038) — the SPA offers
   * "Continue with <agent>" as a one-click reply (D14-10). Present only when
   * this turn's handoff moved the workstream into an agent-initiated invite.
   */
  handoff: z.looseObject({ to: z.string() }).optional(),
});
export type WorkstreamTurnResult = z.infer<typeof WorkstreamTurnResultSchema>;

/**
 * Poll payload: `running` while the agent works, `complete` with `result`,
 * `failed` when the turn errored (the SPA surfaces the send-error state).
 * `events` (ADR-0042) is the live-progress trail — present on any status.
 */
/**
 * Read-side twin of {@link TurnEventSchema}: same required shape, but TOLERANT
 * of unknown keys (AC10 must-ignore).
 *
 * The strict schema is the BFF's defence when narrowing agent output — the
 * agent is the untrusted-ish producer there, and dropping a malformed event is
 * the right call. This one is what the *response* carries, and its consumer is
 * a browser holding a JS bundle we cannot redeploy. Parsing that strictly meant
 * a single unknown key on a single event threw the WHOLE poll response, failing
 * the turn outright — strictly worse than a blanked trail, and unreachable by
 * deploy ordering, since a tab opened before the deploy keeps the old bundle.
 * Tolerating unknown keys makes forward-compatible fields a non-event for every
 * client, permanently, instead of only across one deploy window.
 */
export const TurnEventReadSchema = z.looseObject({
  at: Rfc3339DateTime,
  kind: z.string().min(1).max(40),
  label: z.string().min(1).max(500),
  seq: TurnEventSeq,
  tokens: TurnEventTokens,
});

export const WorkstreamChatPollDataSchema = z.looseObject({
  status: z.enum(['running', 'complete', 'failed']),
  result: WorkstreamTurnResultSchema.optional(),
  events: z.array(TurnEventReadSchema).max(MAX_TURN_EVENTS).optional(),
});
export type WorkstreamChatPollData = z.infer<typeof WorkstreamChatPollDataSchema>;

export const WorkstreamChatPollResponseSchema = z.looseObject({
  data: WorkstreamChatPollDataSchema,
  meta: MetaSchema,
});
export type WorkstreamChatPollResponse = z.infer<typeof WorkstreamChatPollResponseSchema>;

/**
 * `POST /api/v1/workstreams/create/chat/poll` (ADR-0074) — the pre-create
 * companion turn's poll. Same request (`WorkstreamChatPollRequestSchema`) and
 * submit ack (`WorkstreamChatSubmitDataSchema`) as the post-create pair; only
 * the completed `result` differs — it is the `CreateChatData` payload the
 * synchronous `/workstreams/create/chat` used to return, minus `userMessage`
 * (the submit ack already echoed it).
 */
export const CreateChatPollDataSchema = z.looseObject({
  status: z.enum(['running', 'complete', 'failed']),
  result: CreateChatDataSchema.omit({ userMessage: true }).optional(),
  events: z.array(TurnEventReadSchema).max(MAX_TURN_EVENTS).optional(),
});
export type CreateChatPollData = z.infer<typeof CreateChatPollDataSchema>;

export const CreateChatPollResponseSchema = z.looseObject({
  data: CreateChatPollDataSchema,
  meta: MetaSchema,
});
export type CreateChatPollResponse = z.infer<typeof CreateChatPollResponseSchema>;

/**
 * `data` of `GET /api/v1/workstreams/{id}/messages` — the Drop-7 transcript
 * read path (ADR-0047). The BFF invokes the agent's `listMessages` action,
 * which reads `WorkstreamState.recent_messages` — the message log the
 * summarizer writes every completed turn (newest-40 capped, 30-day state
 * TTL). Chronological (oldest first) so the SPA seeds its transcript as-is.
 * Empty for a never-run or idle-aged-out workstream.
 */
export const ListMessagesDataSchema = z.strictObject({
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
});
export type ListMessagesData = z.infer<typeof ListMessagesDataSchema>;

export const ListMessagesResponseSchema = z.looseObject({
  data: ListMessagesDataSchema,
  meta: MetaSchema,
});
export type ListMessagesResponse = z.infer<typeof ListMessagesResponseSchema>;
